import React, { useEffect, useRef, useState } from 'react';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import { Carousel } from 'react-responsive-carousel';
import { SecondaryIconButton } from '@hz-design-system/web-ui';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import CarouselImage from './CarouselImage';

import { TListingGallery } from 'types/TListing';

import Classes from './Carousel.module.scss';
import './ReactCarousel.scss';

type TCarouselProps = {
  imageUrls: TListingGallery['imageUrls'];
  imageSizes?: TListingGallery['media']['imageSizes'];
  onClickItemHandler?: (index: number) => void;
  onChangeHandler?: (index: number) => void;
  index?: number;
  isModal?: boolean;
  alt: string;
};

const SET_SLIDER_TEXT_TIMEOUT = 100;

const CarouselWrapper = ({
  imageUrls = [],
  imageSizes,
  onClickItemHandler,
  onChangeHandler,
  alt,
  index = 0,
  isModal = false,
}: TCarouselProps) => {
  const { t } = useI18nContext();
  const [sliderAnnouncementKey, setSliderAnnouncementKey] = useState(0);
  const [sliderAnnouncementText, setSliderAnnouncementText] = useState('');
  const [preLoadSiblingImages, setPreLoadSiblingImages] = useState(isModal);
  const prevAnnouncementTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nextAnnouncementTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const prevTimeout = prevAnnouncementTimeoutRef;
    const nextTimeout = nextAnnouncementTimeoutRef;
    return () => {
      if (prevTimeout.current !== null) {
        clearTimeout(prevTimeout.current);
      }
      if (nextTimeout.current !== null) {
        clearTimeout(nextTimeout.current);
      }
    };
  }, []);

  if (imageUrls.length <= 1 && !isModal) {
    return null;
  }

  const renderArrowPrevHandler = (onClickHandler, hasPrev) => (
    <>
      <div className={`${Classes.navigationContainer} ${Classes.insideNavigation} ${Classes.navPrev}`}>
        <SecondaryIconButton
          name="arrow-left-regular"
          ariaLabel={t('gallery.carousel.previous')}
          onClick={(e) => {
            if (!hasPrev) return;
            onClickHandler(e);
            setSliderAnnouncementKey((prev) => prev + 1);
            setSliderAnnouncementText('');
            prevAnnouncementTimeoutRef.current = setTimeout(() => {
              setSliderAnnouncementText(t('gallery.carousel.previousImageClick'));
            }, SET_SLIDER_TEXT_TIMEOUT);
          }}
          className={`${Classes.navigationButtons} ${!hasPrev ? Classes.navigationButtonsHidden : ''}`}
        />
      </div>
      <div key={sliderAnnouncementKey} aria-live="polite" aria-atomic="true" className={Classes.srOnly}>
        {sliderAnnouncementText}
      </div>
    </>
  );

  const renderArrowNextHandler = (onClickHandler, hasNext) => (
    <div className={`${Classes.navigationContainer} ${Classes.insideNavigation} ${Classes.navNext}`}>
      <SecondaryIconButton
        name="arrow-right-regular"
        ariaLabel={t('gallery.carousel.next')}
        onClick={(e) => {
          if (!hasNext) return;
          onClickHandler(e);
          setSliderAnnouncementKey((prev) => prev + 1);
          setSliderAnnouncementText('');
          nextAnnouncementTimeoutRef.current = setTimeout(() => {
            setSliderAnnouncementText(t('gallery.carousel.nextImageClick'));
          }, SET_SLIDER_TEXT_TIMEOUT);
        }}
        // @ts-expect-error fixme args signature in library is too strict
        onMouseOver={() => setPreLoadSiblingImages(true)}
        className={`${Classes.navigationButtons} ${!hasNext ? Classes.navigationButtonsHidden : ''}`}
      />
    </div>
  );

  const onSwipeStartHandler = () => {
    setPreLoadSiblingImages(true);
  };

  return (
    <Carousel
      showStatus={false}
      showThumbs={false}
      showArrows
      showIndicators={false}
      className={`${Classes.component} ${isModal ? Classes.dialogCarousel : Classes.pageCarousel}`}
      onClickItem={onClickItemHandler}
      onChange={onChangeHandler}
      selectedItem={index}
      renderArrowPrev={renderArrowPrevHandler}
      renderArrowNext={renderArrowNextHandler}
      onSwipeStart={onSwipeStartHandler}
      useKeyboardArrows
      preventMovementUntilSwipeScrollTolerance
      swipeScrollTolerance={20}
      transitionTime={200}
    >
      {imageUrls.map((imageUrl, imageIndex) => (
        <CarouselImage
          key={imageIndex}
          alt={alt}
          imageUrl={imageUrl}
          imageIndex={imageIndex}
          index={index}
          isModal={isModal}
          imageSizes={imageSizes}
          onEnterKeyPress={onClickItemHandler}
          preLoadSiblingImages={preLoadSiblingImages}
        />
      ))}
    </Carousel>
  );
};

export default CarouselWrapper;
